import React from 'react';
import { Alert, Box, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { surface } from '../../theme';
import { StatTile } from '../../components/analytics/AnalyticsParts';
import { FolderSection, ProjectedRow, hasRevenue } from '../../components/analytics/ChannelRows';
import { formatDate, formatMoney } from '../../utils/analyticsFormat';
import { FolderFilter } from './AnalyticsToolbar';

const BASES = [
  { value: '28', label: '28 days' },
  { value: '90', label: '90 days' },
  { value: 'lifetime', label: 'Lifetime' },
];

function basisDescription(basis, finalizedThrough) {
  const through = formatDate(finalizedThrough);
  return basis === 'lifetime'
    ? `Average daily revenue since each channel first earned, through ${through}.`
    : `Average daily revenue over the last ${basis} days, through ${through}. Channels that started earning more recently are averaged over the days they have earned.`;
}

export default function ProjectionsTab(props) {
  const { summary, loading, visibleGroups, totals, basis, onBasisChange, reload } = props;
  const cut = totals.projectedMonth !== totals.projectedMonthGross;
  const anyRevenue = visibleGroups.some((group) => group.channels.some(hasRevenue));

  return (
    <>
      <Paper
        sx={{
          borderRadius: 2,
          background: surface.glass,
          boxShadow: 2,
          p: { xs: 2, md: 2.5 },
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Project from
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={basis}
            onChange={(_, value) => value && onBasisChange(value)}
            aria-label="Project from"
          >
            {BASES.map((option) => (
              <ToggleButton key={option.value} value={option.value} sx={{ px: 1.5 }}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 320px' }}>
          {basisDescription(basis, summary.range.finalizedThrough)} A month is 30 days. These are
          run-rate estimates, not guarantees.
        </Typography>
      </Paper>

      <FolderFilter {...props} />

      {!anyRevenue ? (
        <Alert severity="info">
          None of the channels shown have revenue yet. Projections appear once a channel has
          revenue from YouTube or an imported export.
        </Alert>
      ) : (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
              mb: 2,
            }}
          >
            <StatTile
              label={cut ? 'Per month · your share' : 'Per month'}
              value={`≈ ${formatMoney(totals.projectedMonth)}`}
              footnote={cut ? `Total ≈ ${formatMoney(totals.projectedMonthGross)}` : null}
            />
            <StatTile
              label={cut ? 'Per year · your share' : 'Per year'}
              value={`≈ ${formatMoney(totals.projectedYear)}`}
              footnote={cut ? `Total ≈ ${formatMoney(totals.projectedYearGross)}` : null}
            />
          </Box>

          {visibleGroups.map((group) => {
            const groupCut = group.projectedMonth !== group.projectedMonthGross;
            return (
              <FolderSection
                key={group.id || 'ungrouped'}
                group={group}
                subtotal={
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {groupCut ? 'Your share' : 'Projected'}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
                      ≈ {formatMoney(group.projectedMonth)} / month
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ≈ {formatMoney(group.projectedYear)} / year
                    </Typography>
                  </>
                }
              >
                {[...group.channels]
                  .sort((a, b) => b.projectedMonth - a.projectedMonth)
                  .map((channel) => (
                    <ProjectedRow
                      key={channel.id}
                      channel={channel}
                      basis={basis}
                      folders={summary.folders}
                      existingTags={summary.tags}
                      onChanged={reload}
                    />
                  ))}
              </FolderSection>
            );
          })}
        </Box>
      )}
    </>
  );
}
